import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, Cloud, CheckCircle2, XCircle, AlertCircle, X } from "lucide-react";
import "@/i18n/fase4";

export default function Inventario() {
  const { t, i18n } = useTranslation();
  const { data } = useSWR("/inventario/sources", fetcher);
  const { data: coverage } = useSWR("/inventario/coverage", fetcher);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const download = async (format) => {
    setDownloading(true);
    try {
      const res = await client.get(`/inventario/export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventario-salnes.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("fase4.inv_export_ok"));
    } catch { toast.error(t("fase4.inv_export_error")); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="inventario-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase4.sidebar_inventario")}</div>
          <h1 className="font-serif text-3xl text-salnes-ink">{t("fase4.inv_title")}</h1>
          <div className="text-sm text-salnes-stone mt-1">{t("fase4.inv_subtitle")}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("csv")} disabled={downloading} data-testid="inv-export-csv" className="rounded-full">{t("fase4.inv_export_csv")}</Button>
          <Button variant="outline" onClick={() => download("json")} disabled={downloading} data-testid="inv-export-json" className="rounded-full">{t("fase4.inv_export_json")}</Button>
        </div>
      </div>

      {data && (
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <SummaryCard label={t("fase4.inv_summary_total")} value={data.summary.total} />
          <SummaryCard label={t("fase4.inv_summary_healthy")} value={`${data.summary.healthy} · ${data.summary.healthy_pct}%`} />
          <SummaryCard label={t("fase4.inv_summary_avg_latency")} value={`${data.summary.avg_latency_ms} ms`} />
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="inv-source-grid">
        {(data?.sources || []).map((s) => (
          <SourceCard key={s.code} source={s} lang={i18n.language} onClick={() => setSelected(s)} />
        ))}
      </div>

      {/* Coverage matrix */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="px-6 py-4 border-b hairline">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone">{t("fase4.inv_coverage_title")}</div>
          <div className="text-sm text-salnes-stone">{t("fase4.inv_coverage_body")}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inv-coverage-table">
            <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-4 py-3">Módulo</th>
                {(coverage?.sources || []).map((s) => (
                  <th key={s} className="text-center px-2 py-3 text-[9px] whitespace-nowrap">{s.replace("tristrash_", "T·").replace("local_", "L·")}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {(coverage?.matrix || []).map((row) => (
                <tr key={row.module}>
                  <td className="px-4 py-3 font-medium text-salnes-ink capitalize">{row.module}</td>
                  {(coverage?.sources || []).map((s) => (
                    <td key={s} className="text-center px-2 py-3">
                      {row.sources.includes(s) ? <span className="text-salnes-emerald">●</span> : <span className="text-salnes-stone/20">·</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <SourceDrawer source={selected} lang={i18n.language} onClose={() => setSelected(null)} />}
    </div>
  );
}

const SummaryCard = ({ label, value }) => (
  <div className="rounded-2xl border hairline bg-white p-6">
    <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">{label}</div>
    <div className="font-serif text-3xl text-salnes-atlantic tabular-nums mt-1">{value}</div>
  </div>
);

const SourceCard = ({ source, lang, onClick }) => {
  const name = source[`name_${lang}`] || source.name_gl;
  const StatusIcon = source.healthy === true ? CheckCircle2 : source.healthy === false ? XCircle : AlertCircle;
  const statusColor = source.healthy === true ? "text-salnes-emerald" : source.healthy === false ? "text-salnes-coral" : "text-salnes-stone";
  const TypeIcon = source.type === "mongo" ? Database : Cloud;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`inv-source-${source.code}`}
      className="text-left rounded-2xl border hairline bg-white p-5 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <TypeIcon size={14} className="text-salnes-stone" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">{source.type}</span>
        </div>
        <StatusIcon size={16} className={statusColor} />
      </div>
      <div className="font-medium text-salnes-ink mb-1">{name}</div>
      <div className="text-[11px] text-salnes-stone font-mono">{source.code}</div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center pt-3 border-t hairline">
        <MiniStat label="ms" value={source.latency_ms ?? "—"} />
        <MiniStat label="rec." value={source.records_count ?? "—"} />
        <MiniStat label="h" value={source.freshness_hours ?? "—"} />
      </div>
    </button>
  );
};

const MiniStat = ({ label, value }) => (
  <div>
    <div className="text-[10px] text-salnes-stone">{label}</div>
    <div className="text-sm font-medium text-salnes-ink tabular-nums">{value}</div>
  </div>
);

const SourceDrawer = ({ source, lang, onClose }) => {
  const { t } = useTranslation();
  const { data } = useSWR(`/inventario/sources/${source.code}/history?days=30`, fetcher);
  const name = source[`name_${lang}`] || source.name_gl;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="inv-source-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-lg bg-white border-l hairline overflow-y-auto">
        <div className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{source.code}</div>
            <div className="font-serif text-lg text-salnes-ink">{name}</div>
          </div>
          <button onClick={onClose} className="text-salnes-stone hover:text-salnes-ink" data-testid="inv-drawer-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row label={t("fase4.inv_drawer_owner")} value={source.owner} />
          <Row label={t("fase4.inv_drawer_frequency")} value={source.frequency_min ? `${source.frequency_min} min` : "on-demand"} />
          <Row label={t("fase4.inv_drawer_upstream")} value={<code className="text-[11px]">{source.upstream_url}</code>} />
          <Row label={t("fase4.inv_card_latency")} value={`${source.latency_ms ?? "—"} ms`} />
          <Row label={t("fase4.inv_card_records")} value={source.records_count ?? "—"} />
          <Row label={t("fase4.inv_card_last_check")} value={source.last_check_at?.slice(0, 19).replace("T", " ") || "—"} />
          <Row label={t("fase4.inv_drawer_history")} value={data ? `${data.uptime_pct ?? "—"}% · ${data.count} amostras` : "—"} />
        </div>
      </aside>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{label}</div>
    <div className="text-sm text-salnes-ink text-right">{value}</div>
  </div>
);
